import { Separator } from '@base-ui/react/separator'
import React from 'react'
import styled, { css } from 'styled-components'
import { semantic } from 'sutra/tokens/colors'
import { spacing } from 'sutra/tokens/spacing'
import { typography } from 'sutra/tokens/typography'

export type DividerOrientation = 'horizontal' | 'vertical'
// Where the label sits along a horizontal divider.
export type DividerLabelAlign = 'start' | 'center' | 'end'

export interface DividerProps {
  // The axis of the line. Base UI's meaning, not antd's, whose `orientation`
  // placed the text; that is `labelAlign` here.
  orientation?: DividerOrientation
  labelAlign?: DividerLabelAlign
  dashed?: boolean
  // Lighter label: regular weight, muted color, smaller size.
  plain?: boolean
  // A label for a horizontal divider, drawn between two line segments.
  children?: React.ReactNode
  className?: string
  style?: React.CSSProperties
}

// Share of the width the short segment takes when the label is aligned to an
// end.
const SHORT_SEGMENT = '5%'

interface LineProps {
  $dashed: boolean
  $vertical?: boolean
}

const line = css<LineProps>`
  box-sizing: border-box;
  margin: 0;
  border-width: 0;
  border-style: ${({ $dashed }) => ($dashed ? 'dashed' : 'solid')};
  border-color: ${semantic.border};
  ${({ $vertical }) =>
    $vertical ? 'border-inline-start-width: 1px;' : 'border-top-width: 1px;'}
`

const Rule = styled(Separator)<LineProps>`
  ${line}
  ${({ $vertical }) =>
    $vertical
      ? css`
          display: inline-block;
          height: 0.9em;
          margin: 0 ${spacing[2]}px;
          vertical-align: middle;
        `
      : css`
          width: 100%;
          margin: ${spacing[6]}px 0;
        `}
`

const Labelled = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin: ${spacing[4]}px 0;
`

const Segment = styled(Separator)<LineProps & { $flex: string }>`
  ${line}
  flex: ${({ $flex }) => $flex};
`

// The second segment is decoration: one separator per divider is enough for
// assistive tech, and it announces ahead of the label.
const TrailingSegment = styled.div<LineProps & { $flex: string }>`
  ${line}
  flex: ${({ $flex }) => $flex};
`

const Label = styled.span<{ $plain: boolean }>`
  padding: 0 ${spacing[4]}px;
  font-family: ${typography.fontFamily};
  white-space: nowrap;
  ${({ $plain }) =>
    $plain
      ? css`
          color: ${semantic.fgMuted};
          font-size: ${typography.text.sm.fontSize}px;
          font-weight: ${typography.weights.regular};
        `
      : css`
          color: ${semantic.fg};
          font-size: ${typography.text.md.fontSize}px;
          font-weight: ${typography.weights.medium};
        `}
`

const segmentFlex = (
  labelAlign: DividerLabelAlign,
): { leading: string; trailing: string } => ({
  leading: labelAlign === 'start' ? `0 0 ${SHORT_SEGMENT}` : '1',
  trailing: labelAlign === 'end' ? `0 0 ${SHORT_SEGMENT}` : '1',
})

export const Divider = ({
  orientation = 'horizontal',
  labelAlign = 'center',
  dashed = false,
  plain = false,
  children,
  className,
  style,
}: DividerProps): React.ReactElement => {
  const vertical = orientation === 'vertical'
  if (vertical || !children) {
    return (
      <Rule
        orientation={orientation}
        $vertical={vertical}
        $dashed={dashed}
        className={className}
        style={style}
      />
    )
  }
  const flex = segmentFlex(labelAlign)
  return (
    <Labelled className={className} style={style}>
      <Segment $dashed={dashed} $flex={flex.leading} />
      <Label $plain={plain}>{children}</Label>
      <TrailingSegment aria-hidden $dashed={dashed} $flex={flex.trailing} />
    </Labelled>
  )
}

Divider.displayName = 'Divider'
